import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

export default function Navbar() {
  const { cartCount, openDrawer, clearCart } = useCart();
  const navigate = useNavigate();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // AUTH STATE
  const [user, setUser] = useState(() => {
    const name = localStorage.getItem("userName");
    const email = localStorage.getItem("userEmail");
    const token = localStorage.getItem("userToken");
    return token ? { name, email } : null;
  });

  const isAdmin = localStorage.getItem("isAdmin") === "true";

  // Detect scroll for navbar styling
  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 10);
    }
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Logout
  const logout = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("isAdmin");
    clearCart();
    setUser(null);

    navigate("/");
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 border-b
      ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 shadow-sm border-gray-200"
          : "backdrop-blur-2xl bg-white/40 border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 h-16 md:h-[72px] flex items-center justify-between">
        
        {/* LOGO */}
        <Link
          to="/"
          className="text-[24px] font-semibold tracking-tight text-gray-900 hover:opacity-90 transition select-none"
        >
          Relax<span className="text-rose-600">B</span>
        </Link>

        {/* DESKTOP NAV */}
        <div className="hidden md:flex items-center gap-8 text-[15px] font-medium">

          <NavItem to="/">Home</NavItem>
          <NavItem to="/products">Products</NavItem>
          <NavItem to="/contact">Contact</NavItem>

          {user && <NavItem to="/my-orders">My Orders</NavItem>}
          {isAdmin && <NavItem to="/admin">Admin</NavItem>}

          {/* AUTH SECTION */}
          {!user ? (
            <>
              <NavItem to="/login">Login</NavItem>
              <NavItem to="/register">Register</NavItem>
            </>
          ) : (
            <>
              <span className="text-gray-700">
                Hi, <span className="font-semibold">{user.name?.split(" ")[0]}</span>
              </span>
              <button
                onClick={logout}
                className="text-gray-600 hover:text-black transition"
              >
                Logout
              </button>
            </>
          )}

          {/* CART BUTTON */}
          <button
            onClick={openDrawer}
            className="relative flex items-center justify-center w-10 h-10 rounded-full 
            border border-gray-300 hover:bg-gray-100 transition"
          >
            <span className="text-[20px]">🛒</span>
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-rose-600 text-white 
              text-[10px] px-1.5 py-[1px] rounded-full font-semibold">
                {cartCount}
              </span>
            )}
          </button>
        </div>

        {/* MOBILE MENU BUTTON */}
        <button
          className="md:hidden text-3xl text-gray-800"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? "✕" : "☰"}
        </button>
      </div>

      {/* MOBILE MENU */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden 
        ${mobileOpen ? "max-h-96" : "max-h-0"}`}
      >
        <div className="px-4 py-4 flex flex-col gap-4 bg-white/90 backdrop-blur-xl border-t">

          <NavItem to="/" mobile>Home</NavItem>
          <NavItem to="/products" mobile>Products</NavItem>
          <NavItem to="/contact" mobile>Contact</NavItem>

          {user && <NavItem to="/my-orders" mobile>My Orders</NavItem>}
          {isAdmin && <NavItem to="/admin" mobile>Admin</NavItem>}

          {!user ? (
            <>
              <NavItem to="/login" mobile>Login</NavItem>
              <NavItem to="/register" mobile>Register</NavItem>
            </>
          ) : (
            <>
              <span className="text-gray-700 font-medium">
                Signed in as {user.name}
              </span>

              <button
                onClick={() => {
                  logout();
                  setMobileOpen(false);
                }}
                className="text-left text-gray-700 hover:text-black transition"
              >
                Logout
              </button>
            </>
          )}

          <button
            onClick={() => {
              openDrawer();
              setMobileOpen(false);
            }}
            className="flex items-center gap-3 border px-3 py-2 rounded-xl 
            hover:bg-gray-100 transition"
          >
            🛒 <span className="text-sm">Cart ({cartCount})</span>
          </button>

        </div>
      </div>
    </nav>
  );
}

/* REUSABLE NAV ITEM */
function NavItem({ to, children, mobile }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `transition flex items-center
        ${mobile ? "text-[16px] text-gray-700" : "text-gray-600 hover:text-gray-900"}
        ${isActive ? "font-semibold text-gray-900" : ""}`
      }
      onClick={() => window.scrollTo(0, 0)}
    >
      {children}
    </NavLink>
  );
}
